import React, { useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Checkbox,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';
import { useImmer } from "use-immer";

import { useMisc } from 'store';
import { Page, FormLabel } from 'components';
import { callApi, useGoBack } from 'utils';

const crossDayConfirmationCode = 'batch_add_cross_day_schedule_9to9';

const buildBatchPayload = (values) => {
  const data = {
    ...values,
    ant_cfg: values.ant_cfg === '_cust_' ? values.ant_cfg_cust.trim() : values.ant_cfg,
    // not saving \n for psql display and easy backup of job table
    extra: values.extra.replaceAll('\n', '@@'),
  };
  delete data.ant_cfg_cust;
  return data;
};

const BatchAdd = () => {
  const location = useLocation();
  const misc = useMisc((state) => state.data);
  const goBack = useGoBack('/jobs');

  const isEditing = location.pathname.includes('/batchedit');

  const [formState, setFormState] = useImmer({
    values: {
      org: 1,
      ant_cfg: 'auto.yml',
      ant_cfg_cust: '',
      extra: '',
      content: '',
      cross_day_schedule: false,
    },
    errors: null,
  });
  const [phase, setPhase] = useState('idle');
  const [pendingPayload, setPendingPayload] = useState(null);
  const inFlightRef = useRef(false);

  const {values: vals, errors: errs} = formState;
  const saving = phase === 'submitting';

  const handleChange = useCallback((ev) => {
    const t = ev.target;
    setFormState(draft => {
      draft.values[t.name] = t.type === 'checkbox' ? t.checked : t.value;
    });
  }, [setFormState]);

  Boolean(errs) && console.log(errs);

  const submitBatch = useCallback(async (data) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setPhase('submitting');
    try {
      const res = await callApi(`/jobs/batch/${isEditing ? 'edit' : 'add'}`, 'POST', data);
      if (res.errors) {
        setFormState(draft => {
          draft.errors = res.errors;
        });
        setPendingPayload(null);
        setPhase('idle');
        return;
      }

      const confirmation = res.confirmation_required;
      if (
        !isEditing &&
        !data.cross_day_schedule &&
        confirmation?.code === crossDayConfirmationCode &&
        confirmation.field === 'cross_day_schedule' &&
        confirmation.required_value === true
      ) {
        setPendingPayload(data);
        setPhase('awaiting_confirmation');
        return;
      }
      if (res.success === true) {
        goBack();
        return;
      }
      setFormState(draft => {
        draft.errors = {content: 'Unexpected server response'};
      });
      setPendingPayload(null);
      setPhase('idle');
    } catch {
      setPhase('idle');
    } finally {
      inFlightRef.current = false;
    }
  }, [goBack, isEditing, setFormState]);

  const handleSubmit = useCallback((ev) => {
    ev.preventDefault();
    if (phase !== 'idle') return;
    submitBatch(buildBatchPayload(vals));
  }, [phase, submitBatch, vals]);

  const handleCancelConfirmation = useCallback(() => {
    if (phase !== 'awaiting_confirmation') return;
    setPendingPayload(null);
    setPhase('idle');
  }, [phase]);

  const handleConfirmCrossDay = useCallback(() => {
    if (phase !== 'awaiting_confirmation' || !pendingPayload || inFlightRef.current) return;
    const data = {...pendingPayload, cross_day_schedule: true};
    setFormState(draft => {
      draft.values.cross_day_schedule = true;
    });
    setPendingPayload(null);
    submitBatch(data);
  }, [pendingPayload, phase, setFormState, submitBatch]);

  const title = isEditing ? "Batch Edit" : "Batch Add";
  const hasError = (field) => Boolean(errs?.[field]);

  return (
    <Page styles={{padding: 3}} title={title}>
      <Card>
        <form onSubmit={handleSubmit}>
          <CardHeader title={title} />
          <Divider />
          <CardContent>
            {!isEditing && (
              <Stack direction="row" spacing={2}>
                <Stack direction="row" spacing={1}>
                  <FormLabel gutterBottom variant="subtitle1">Organization:</FormLabel>
                  <Select
                    name="org"
                    value={vals.org}
                    size="small"
                    onChange={handleChange}
                  >
                  {Object.entries(misc['org']).map(([val, display]) => (
                    <MenuItem value={val|0} key={val}>{display}</MenuItem>
                  ))}
                  </Select>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <FormLabel gutterBottom variant="subtitle1">Page Actions:</FormLabel>
                  <Select
                    name="ant_cfg"
                    value={vals.ant_cfg}
                    size="small"
                    onChange={handleChange}
                    displayEmpty
                  >
                  {Object.entries(misc['ant_cfg']).map(([val, display]) => (
                    <MenuItem value={val} key={val}>{display}</MenuItem>
                  ))}
                  </Select>
                  {vals.ant_cfg === '_cust_' && (
                  <OutlinedInput
                    sx={{ml: '8px', p: 0, width: 200}}
                    name="ant_cfg_cust"
                    value={vals.ant_cfg_cust}
                    error={hasError("ant_cfg_cust")}
                    size="small"
                    inputProps={{ style: {textAlign: 'left'} }}
                    onChange={handleChange}
                    required
                  />
                  )}
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="cross_day_schedule"
                      checked={vals.cross_day_schedule}
                      onChange={handleChange}
                    />
                  }
                  label="跨日排期单"
                />
              </Stack>
            )}
            <div style={{margin: '8px'}}>
              <FormControl sx={{width: '100%'}}>
                <OutlinedInput
                  sx={{padding: 0, width: '100%'}}
                  name="content"
                  value={vals.content}
                  inputProps={{ style: {padding: '8.5px 14px'} }}
                  error={hasError("content")}
                  size="small"
                  rows={10}
                  onChange={handleChange}
                  multiline
                />
                <FormHelperText sx={{marginLeft: 0}}>
                  {errs?.content ? (
                    <Box component="span" sx={{color: 'error.main'}}>{errs.content}.</Box>
                  ) : (
                    <span>Headers must be put as first line, tab separated.</span>
                  )}
                </FormHelperText>
              </FormControl>
            </div>
            {!isEditing && (
            <Stack direction="row" spacing={1}>
              <FormLabel gutterBottom variant="subtitle1">Extra Info:</FormLabel>
              <OutlinedInput
                sx={{padding: 0, width: 800}}
                name="extra"
                value={vals.extra}
                error={hasError("extra")}
                size="small"
                inputProps={{ style: {padding: '8.5px 14px'} }}
                onChange={handleChange}
                multiline
              />
            </Stack>
            )}
          </CardContent>
          <Divider />
          <CardActions>
            <Button
              color="primary"
              variant="outlined"
              type="submit"
              disabled={phase !== 'idle'}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              color="primary"
              variant="outlined"
              type="button"
              onClick={goBack}
              disabled={phase !== 'idle'}
            >
              Cancel
            </Button>
          </CardActions>
        </form>
      </Card>
      <Dialog
        open={phase === 'awaiting_confirmation'}
        onClose={handleCancelConfirmation}
        aria-labelledby="batch-cross-day-confirmation-title"
      >
        <DialogTitle id="batch-cross-day-confirmation-title">跨日排期确认</DialogTitle>
        <DialogContent>
          <DialogContentText>
            检测到批量内容中有 09:00-09:00 的任务，但尚未勾选“跨日排期单”。是否勾选并继续上传？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={handleCancelConfirmation}>取消</Button>
          <Button type="button" onClick={handleConfirmCrossDay}>勾选并继续上传</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default BatchAdd;
